// @perlab/cross-tab — Tab presence registry (heartbeat-only).
//
// Per spec §3.3 (28_Parallel_Work_Architecture_Tier1_V3.md, unchanged from v2):
//   - 5s heartbeat interval via setInterval
//   - 12s eviction window — tabs with lastSeen > 12s ago are removed
//   - pagehide → publish 'tab.bye' (best-effort; NOT authoritative)
//   - visibilitychange visible → immediate heartbeat
//   - wake-from-sleep stagger: Math.random() * 2000 ms before resuming
//     normal heartbeat cadence — avoids heartbeat storms when 100 tabs
//     unsleep simultaneously
//
// Authoritative GC is the heartbeat absence — 'tab.bye' may not arrive
// (BFCache, kill -9, mobile background). The registry self-heals from
// missing heartbeats alone.

import type { CrossTabBus } from './bus.js';

const HEARTBEAT_MS = 5_000;
const EVICT_AFTER_MS = 12_000;
const SWEEP_MS = 3_000;
const WAKE_STAGGER_MAX_MS = 2_000;

export interface TabHelloPayload {
  tabId: string;
  contextKey: string;
  ts: number;
}

export interface TabByePayload {
  tabId: string;
}

export interface TabRecord extends TabHelloPayload {
  lastSeen: number;
}

export interface TabRegistry {
  /** Map of open peer tabs keyed by tabId. Does NOT include the local tab. */
  readonly openTabs: ReadonlyMap<string, TabRecord>;
  /** Stop heartbeats + sweeps. Idempotent. */
  stop(): void;
  /** Test hook — force-fire one heartbeat now. */
  __forceHeartbeat(): void;
  /** Test hook — force one sweep pass now (uses current Date.now()). */
  __forceSweep(): void;
}

export interface CreateTabRegistryOpts {
  bus: CrossTabBus;
  /**
   * Current contextKey for this tab. Re-read on every heartbeat so route
   * changes propagate without explicit invalidation. Function not value so
   * callers can pass an always-current getter.
   */
  getContextKey: () => string;
}

/**
 * Create a tab registry. Starts heartbeating immediately. Returns handle
 * with stop() + test hooks.
 *
 * Caller MUST keep a reference until shutdown — losing the reference does
 * NOT auto-clean (timers will keep firing).
 */
export function createTabRegistry(opts: CreateTabRegistryOpts): TabRegistry {
  const { bus, getContextKey } = opts;
  const openTabs = new Map<string, TabRecord>();

  // Subscriptions
  const offHello = bus.subscribe<TabHelloPayload>('tab.hello', (env) => {
    openTabs.set(env.payload.tabId, {
      tabId: env.payload.tabId,
      contextKey: env.payload.contextKey,
      ts: env.payload.ts,
      lastSeen: env.payload.ts,
    });
  });
  const offBye = bus.subscribe<TabByePayload>('tab.bye', (env) => {
    // Best-effort: trust 'bye' if it arrives, but heartbeat-absence is the
    // authoritative GC. Either path eventually removes the entry.
    openTabs.delete(env.payload.tabId);
  });

  function sendHeartbeat(): void {
    bus.publish<TabHelloPayload>('tab.hello', {
      tabId: bus.tabId,
      contextKey: getContextKey(),
      ts: Date.now(),
    });
  }

  function sweep(): void {
    const cutoff = Date.now() - EVICT_AFTER_MS;
    for (const [k, v] of openTabs) {
      if (v.lastSeen < cutoff) openTabs.delete(k);
    }
  }

  // Initial heartbeat at construction so peers learn about us without
  // waiting one full interval.
  sendHeartbeat();

  // Timers. Stored so stop() can clear them.
  const heartbeatTimer = setInterval(sendHeartbeat, HEARTBEAT_MS);
  const sweepTimer = setInterval(sweep, SWEEP_MS);

  // Wake-stagger: when the tab becomes visible after being hidden, fire one
  // immediate heartbeat then arm a random-stagger heartbeat to spread load
  // across N peers reawakening simultaneously.
  let wakeStaggerArmed = true;
  let wakeStaggerTimer: ReturnType<typeof setTimeout> | null = null;
  const onVisibilityChange = (): void => {
    if (typeof document === 'undefined') return;
    if (document.visibilityState !== 'visible') {
      wakeStaggerArmed = true;
      return;
    }
    // Immediate beat — let peers update lastSeen for this tab right away.
    sendHeartbeat();
    if (!wakeStaggerArmed) return;
    wakeStaggerArmed = false;
    const jitter = Math.random() * WAKE_STAGGER_MAX_MS;
    wakeStaggerTimer = setTimeout(() => {
      sendHeartbeat();
      wakeStaggerArmed = true;
      wakeStaggerTimer = null;
    }, jitter);
  };

  // pagehide is the right unload signal — beats 'unload' on mobile + BFCache.
  // Still best-effort; heartbeat-absence is authoritative.
  const onPageHide = (): void => {
    bus.publish<TabByePayload>('tab.bye', { tabId: bus.tabId });
  };

  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', onVisibilityChange);
  }
  if (typeof window !== 'undefined') {
    window.addEventListener('pagehide', onPageHide);
  }

  let stopped = false;
  function stop(): void {
    if (stopped) return;
    stopped = true;
    clearInterval(heartbeatTimer);
    clearInterval(sweepTimer);
    if (wakeStaggerTimer !== null) {
      clearTimeout(wakeStaggerTimer);
      wakeStaggerTimer = null;
    }
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', onVisibilityChange);
    }
    if (typeof window !== 'undefined') {
      window.removeEventListener('pagehide', onPageHide);
    }
    offHello();
    offBye();
  }

  return {
    openTabs,
    stop,
    __forceHeartbeat: sendHeartbeat,
    __forceSweep: sweep,
  };
}
